import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const MicroBiologyCoursePage: React.FC = () => {
  const microBiologyData = {
    title: "Bachelor of Science in Microbiology",
    shortName: "B.Sc Microbiology",
    duration: "3 Years",
    description: "A comprehensive program studying microorganisms and their effects on humans, animals, plants, and the environment for healthcare and research applications.",
    overview: "B.Sc Microbiology is a specialized life science program that focuses on the study of microscopic organisms including bacteria, viruses, fungi, protozoa, and algae. Students learn about microbial structure, function, genetics, metabolism, and interactions with other organisms. The course covers applied microbiology in healthcare, pharmaceuticals, food technology, environmental science, and biotechnology. Graduates are equipped to work in diagnostic laboratories, research institutions, pharmaceutical companies, and quality control departments.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Mathematics as additional subject preferred",
      "Age: 17-25 years",
      "English proficiency required",
      "Science aptitude essential"
    ],
    careerProspects: {
      roles: [
        "Microbiologist",
        "Medical Laboratory Technician",
        "Research Scientist",
        "Quality Control Analyst",
        "Clinical Microbiologist",
        "Food Microbiologist",
        "Environmental Microbiologist",
        "Pharmaceutical Microbiologist",
        "Laboratory Manager",
        "Biotechnology Researcher"
      ],
      industries: [
        "Healthcare and Hospitals",
        "Pharmaceutical Companies",
        "Diagnostic Laboratories",
        "Research Institutions",
        "Food and Beverage Industry",
        "Environmental Agencies",
        "Biotechnology Companies",
        "Government Health Departments",
        "Academic Institutions",
        "Quality Control Labs"
      ],
      averageSalary: "₹2.5-6 LPA (varies by specialization and experience)",
      topRecruiters: [
        "Dr. Reddy's Laboratories",
        "Cipla",
        "Biocon",
        "SRL Diagnostics",
        "Metropolis Healthcare",
        "ICMR",
        "AIIMS",
        "Apollo Hospitals",
        "Nestle India",
        "Indian Council of Agricultural Research"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "General Microbiology",
          "Cell Biology",
          "Biochemistry",
          "Organic Chemistry",
          "Physics",
          "Mathematics",
          "English",
          "Environmental Studies"
        ]
      },
      {
        year: 2,
        subjects: [
          "Bacteriology",
          "Virology",
          "Mycology",
          "Parasitology",
          "Immunology",
          "Genetics",
          "Molecular Biology",
          "Microbial Physiology"
        ]
      },
      {
        year: 3,
        subjects: [
          "Medical Microbiology",
          "Industrial Microbiology",
          "Food Microbiology",
          "Environmental Microbiology",
          "Microbial Biotechnology",
          "Research Methodology",
          "Bioinformatics",
          "Project Work/Internship"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Microbiology",
      "Merit-based selection or entrance exam",
      "Counseling process (if applicable)",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission",
      "Orientation program",
      "Commence academic session"
    ],
    topColleges: [
      {
        name: "Loyola College",
        location: "Chennai, Tamil Nadu",
        fees: "₹1.5 LPA",
        ranking: "Top Autonomous College"
      },
      {
        name: "St. Xavier's College",
        location: "Mumbai, Maharashtra",
        fees: "₹1.8 LPA",
        ranking: "Prestigious Institution"
      },
      {
        name: "Presidency College",
        location: "Chennai, Tamil Nadu",
        fees: "₹50,000 PA",
        ranking: "Government College"
      },
      {
        name: "Fergusson College",
        location: "Pune, Maharashtra",
        fees: "₹1.2 LPA",
        ranking: "Well Established"
      },
      {
        name: "Madras Christian College",
        location: "Chennai, Tamil Nadu",
        fees: "₹1.3 LPA",
        ranking: "Autonomous College"
      }
    ],
    skills: [
      "Laboratory Techniques",
      "Microscopy",
      "Microbial Culturing",
      "Analytical Skills",
      "Research Methodology",
      "Data Analysis",
      "Quality Control",
      "Scientific Documentation",
      "Problem Solving",
      "Attention to Detail",
      "Safety Protocols",
      "Computer Applications"
    ],
    futureScope: "Microbiology is experiencing rapid growth with applications in personalized medicine, antimicrobial resistance research, probiotics development, environmental bioremediation, and biotechnology. Emerging areas include microbiome research, synthetic biology, and nanotechnology applications. The COVID-19 pandemic has highlighted the critical importance of microbiologists in public health and disease control, creating numerous opportunities in diagnostic development and infectious disease research.",
    faqs: [
      {
        question: "What is the scope after B.Sc Microbiology?",
        answer: "Graduates can pursue M.Sc Microbiology, work in diagnostic labs, pharmaceutical companies, research institutions, or government health departments. Further studies include PhD, MBA in biotechnology, or specialized diplomas in clinical microbiology."
      },
      {
        question: "Is there good job security in microbiology?",
        answer: "Yes, microbiology offers excellent job security due to continuous demand in healthcare, pharmaceuticals, food industry, and environmental monitoring. The field is essential for public health and safety."
      },
      {
        question: "Can I work abroad with a B.Sc Microbiology degree?",
        answer: "Yes, many countries have opportunities for microbiologists. You may need additional certifications or equivalency examinations depending on the destination country's requirements."
      },
      {
        question: "What are the research opportunities in microbiology?",
        answer: "Research opportunities exist in areas like infectious diseases, antimicrobial resistance, vaccine development, probiotics, environmental microbiology, and biotechnology applications."
      },
      {
        question: "How important are practical skills in microbiology?",
        answer: "Practical skills are extremely important. Most microbiology jobs require hands-on laboratory experience with sterile techniques, culturing, microscopy, and analytical equipment operation."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {microBiologyData.title} ({microBiologyData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={microBiologyData} />
      </div>
    </div>
  );
};

export default MicroBiologyCoursePage;